"use client"
import { type CSSProperties, type ReactNode } from "react"
import { usePolicyHoverSwap } from "./policy/policyHoverSwap"
import { usePowerHoverSwap } from "./power/powerHoverSwap"
import {
  useMetricHoverSwap,
  type HoverableMetricId,
} from "./metrics/metricHoverSwap"

// The HUD's in-canvas hover takeovers (policy/metric details over the treemap
// slot, the treemap takeover over the left column) only SET on hover; nothing
// clears until the pointer leaves this whole zone — which wraps the entire
// policies-and-treemap row. Crossing dead space — between policy rows, between
// the panel and the stat row, between the four metrics, and across the gap
// between the two columns — therefore holds the last takeover instead of
// flashing the idle state back for a few pixels of travel. Handoffs happen on
// arrival instead: entering the treemap column swaps a policy/metric detail
// for the power takeover in one beat (ClaimPowerHoverOnEnter below), entering
// the left column dismisses the treemap takeover (built into
// PowerHoverSwapTarget).
export const HoverSwapStickyZone = ({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) => {
  const policySwap = usePolicyHoverSwap()
  const metricSwap = useMetricHoverSwap()
  const powerSwap = usePowerHoverSwap()
  return (
    <div
      className={className}
      onMouseLeave={() => {
        policySwap?.setHoveredPolicyId(null)
        policySwap?.setHoveredStatus(null)
        metricSwap?.setHoveredMetric(null)
        powerSwap?.setHoveredItem(null)
      }}
    >
      {children}
    </div>
  )
}

// How far (px) the treemap column's hover surface reaches PAST the
// visualization on each side, so the power takeover claims the pointer
// before it touches the treemap itself. `left` reaches into the 20px gap
// between the columns — halfway, so the policy squares' own extended
// hitboxes (see PolicyItem's hitExtension) and this surface tile the gap
// with only a sliver of hold-last-state space between them.
export const POWER_HOVER_CLAIM_EXTEND_PX = {
  left: 10,
  right: 8,
  top: 8,
  bottom: 8,
}

// Wraps the treemap column. Entering it swaps the takeovers in ONE beat:
// any policy/metric detail is dismissed (the treemap underneath it is
// visibility-hidden, so the detail can't be escaped any other way) AND the
// distribution-of-power takeover comes up immediately as the whole-chart
// "chart" hover — no waiting for the pointer to reach a specific treemap
// cell. The treemap's own block-level mousemove hit test (PowerTreemap)
// refines the highlight to the lab under the pointer, and the claim below
// is a functional `prev ?? chart` update, so the two can land in either
// order within one gesture without the claim erasing the lab hover. The
// hover surface is inflated past the column by POWER_HOVER_CLAIM_EXTEND_PX
// (padding + matching negative margins, so nothing visible moves);
// `className` styles the inner, layout-true column box.
export const ClaimPowerHoverOnEnter = ({
  className,
  style,
  children,
}: {
  className?: string
  // Inline style for the inner content box (e.g. the mobile dashboard sets a
  // narrower power-column width than the desktop's w-[220px] class).
  style?: CSSProperties
  children: ReactNode
}) => {
  const policySwap = usePolicyHoverSwap()
  const metricSwap = useMetricHoverSwap()
  const powerSwap = usePowerHoverSwap()
  const { left, right, top, bottom } = POWER_HOVER_CLAIM_EXTEND_PX
  return (
    <div
      className="shrink-0"
      style={{
        padding: `${top}px ${right}px ${bottom}px ${left}px`,
        margin: `${-top}px ${-right}px ${-bottom}px ${-left}px`,
      }}
      onMouseOver={() => {
        if (policySwap?.hoveredPolicyId) policySwap.setHoveredPolicyId(null)
        if (policySwap?.hoveredStatus) policySwap.setHoveredStatus(null)
        if (metricSwap?.hoveredMetric) metricSwap.setHoveredMetric(null)
        // Functional update: when the pointer enters the column directly
        // onto a treemap cell, this mouseover and the treemap's own
        // mousemove land in the same gesture, and a plain guard on the
        // render's (stale, null) hoveredItem would overwrite the cell's
        // just-set lab hover with the generic chart claim — leaving the
        // entry cell without its region highlight until the pointer moves
        // again. `prev ?? chart` can only fill an actual gap.
        powerSwap?.setHoveredItem(prev => prev ?? { kind: "chart" })
      }}
    >
      <div className={className} style={style}>{children}</div>
    </div>
  )
}

// Hover props for a stat-row metric inside the sticky zone: entering swaps the
// metric's definition in and dismisses any policy detail or treemap hover
// (the detail families share one slot). `valueText` is the indicator's
// currently displayed value, formatted exactly as the stat row shows it
// ("62%", "$45K") — the detail leads with it. No leave handler — the zone
// clears on exit. Returns undefined outside a <MetricHoverSwap>, where the
// indicator keeps its floating tooltip.
export const useMetricHoverTrigger = (id: HoverableMetricId, valueText: string) => {
  const metricSwap = useMetricHoverSwap()
  const policySwap = usePolicyHoverSwap()
  const powerSwap = usePowerHoverSwap()
  if (!metricSwap) return undefined
  return {
    // Feeds the /analytics "Hovered elements" panel via ui/hoverTracking.tsx.
    "data-track-hover": `hud-metric:${id}`,
    onMouseEnter: () => {
      metricSwap.setHoveredMetric({ id, valueText })
      policySwap?.setHoveredPolicyId(null)
      policySwap?.setHoveredStatus(null)
      powerSwap?.setHoveredItem(null)
    },
  }
}
